import { useEffect, useRef } from 'react';

/**
 * Custom hook for enabling two-finger horizontal scrolling on all devices
 * 
 * This hook detects two-finger touch gestures and translates them into
 * horizontal scroll movements. It works on mobile, tablet, and desktop trackpads
 * and does not interfere with:
 * - One-finger normal scrolling
 * - Vertical scrolling
 * - Existing react-slick sliders
 * - Native trackpad horizontal scrolling
 * 
 * @param {React.RefObject} containerRef - Ref to the scrollable container element
 * @returns {void}
 */
const useTwoFingerScroll = (containerRef) => {
  const isGestureActive = useRef(false);
  const initialTouches = useRef(null);
  const initialScrollLeft = useRef(0);
  const lastScrollLeft = useRef(0);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) {
      return;
    }

    /**
     * Handle touch start - detect two-finger gesture
     */
    const handleTouchStart = (e) => {
      // Only activate if exactly 2 touches
      if (e.touches.length === 2) {
        isGestureActive.current = true;
        
        // Store initial touch positions
        initialTouches.current = {
          touch1: { x: e.touches[0].clientX, y: e.touches[0].clientY },
          touch2: { x: e.touches[1].clientX, y: e.touches[1].clientY }
        };
        
        // Store initial scroll position
        initialScrollLeft.current = container.scrollLeft;
        lastScrollLeft.current = container.scrollLeft;
        
        // Prevent default to avoid conflicts with native scrolling
        e.preventDefault();
      } else {
        // Reset if not two fingers
        isGestureActive.current = false;
        initialTouches.current = null;
      }
    };

    /**
     * Handle touch move - translate two-finger movement to horizontal scroll
     */
    const handleTouchMove = (e) => {
      // Only process if gesture is active and we have exactly 2 touches
      if (!isGestureActive.current || e.touches.length !== 2) {
        return;
      }

      // Calculate center point of the two touches
      const currentTouch1 = { x: e.touches[0].clientX, y: e.touches[0].clientY };
      const currentTouch2 = { x: e.touches[1].clientX, y: e.touches[1].clientY };
      
      const currentCenterX = (currentTouch1.x + currentTouch2.x) / 2;
      const initialCenterX = (initialTouches.current.touch1.x + initialTouches.current.touch2.x) / 2;
      
      // Calculate horizontal movement
      const deltaX = currentCenterX - initialCenterX;
      
      // Calculate vertical movement to detect if user is scrolling vertically
      const currentCenterY = (currentTouch1.y + currentTouch2.y) / 2;
      const initialCenterY = (initialTouches.current.touch1.y + initialTouches.current.touch2.y) / 2;
      const deltaY = Math.abs(currentCenterY - initialCenterY);
      
      // Only scroll horizontally if horizontal movement is greater than vertical
      // This prevents interference with vertical scrolling
      if (Math.abs(deltaX) > deltaY) {
        // Calculate new scroll position
        const newScrollLeft = initialScrollLeft.current - deltaX;
        
        // Apply smooth scrolling
        container.scrollLeft = newScrollLeft;
        lastScrollLeft.current = newScrollLeft;
        
        // Prevent default to avoid page scroll
        e.preventDefault();
      }
    };

    /**
     * Handle touch end - reset gesture state
     */
    const handleTouchEnd = (e) => {
      // Reset gesture state when fingers are lifted
      if (e.touches.length < 2) {
        isGestureActive.current = false;
        initialTouches.current = null;
      }
    };

    // Attach event listeners
    container.addEventListener('touchstart', handleTouchStart, { passive: false });
    container.addEventListener('touchmove', handleTouchMove, { passive: false });
    container.addEventListener('touchend', handleTouchEnd, { passive: true });

    // Cleanup
    return () => {
      container.removeEventListener('touchstart', handleTouchStart);
      container.removeEventListener('touchmove', handleTouchMove);
      container.removeEventListener('touchend', handleTouchEnd);
    };
  }, [containerRef]);
};

export default useTwoFingerScroll;

